import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { SummarizerConfig } from '../../types/summarizer';
import { useConfigStore } from '../../core/store/useConfigStore';
import { getToolById } from '../../config/summarizers';

interface ConfigContextValue {
  configs: Record<string, SummarizerConfig>;
  activeConfig: SummarizerConfig | null;
  isLoading: boolean;
  error: string | null;
  loadConfig: (toolId: string) => Promise<SummarizerConfig | null>;
  saveConfig: (toolId: string, config: SummarizerConfig) => Promise<boolean>;
  updateConfig: (updates: Partial<SummarizerConfig>) => void;
  resetToDefault: (toolId: string) => Promise<boolean>;
  exportConfig: (toolId: string) => string;
  importConfig: (jsonString: string) => Promise<boolean>;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

/**
 * Hook to use configuration context
 */
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
  initialToolId?: string;
}

/**
 * ConfigProvider Component
 * 
 * Provides configuration context to all child components
 * Handles loading configurations on mount
 */
export const ConfigProvider: React.FC<ConfigProviderProps> = ({ 
  children, 
  initialToolId 
}) => {
  const {
    configs,
    activeConfig,
    isLoading,
    error,
    loadConfigs,
    loadConfig,
    saveConfig,
    updateActiveConfig,
    resetToDefault,
    exportConfig,
    importConfig,
    setActiveConfig
  } = useConfigStore();

  // Load all configurations on mount
  useEffect(() => {
    loadConfigs().catch(error => {
      console.warn('Failed to load configs from API, using defaults:', error);
    });
  }, []);

  // Load initial tool configuration if specified
  useEffect(() => {
    if (initialToolId && configs[initialToolId]) {
      setActiveConfig(configs[initialToolId]);
    } else if (initialToolId) {
      loadConfig(initialToolId).then(config => {
        if (config) {
          setActiveConfig(config);
        } else {
          // If API fails, try to get default config from local registry
          const defaultConfig = getToolById(initialToolId);
          if (defaultConfig) {
            setActiveConfig(defaultConfig);
          }
        }
      }).catch(error => {
        console.warn(`Failed to load config for ${initialToolId}, trying defaults:`, error);
        const defaultConfig = getToolById(initialToolId);
        if (defaultConfig) {
          setActiveConfig(defaultConfig);
        }
      });
    }
  }, [initialToolId, configs]);

  const contextValue: ConfigContextValue = {
    configs,
    activeConfig,
    isLoading,
    error,
    loadConfig,
    saveConfig,
    updateConfig: updateActiveConfig,
    resetToDefault,
    exportConfig,
    importConfig
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigProvider;